import React, {createContext, useContext, useReducer, useEffect} from 'react';
import PropTypes from 'prop-types';
import Honeybadger from '@honeybadger-io/js';
import RegistryCoreApiService from '../services/RegistryCoreApi';
import MemberContext from './MemberContext';
import {
  formatGvrUrl,
  formatWwsUrl,
  getHasCashRegistry,
  getHasEverHadCashRegistry,
  sortByTypeTierAndName,
} from './utils/couple';

const initialState = {
  coupleId: null,
  fetchingCouple: true,
  fianceFirstName: '',
  firstName: '',
  gvrUrl: null,
  hasCashRegistry: false,
  hasEverHadCashRegistry: false,
  hasTransactionalRegistry: false,
  registries: [],
  wwsUrl: null,
};

const Context = createContext(initialState);

export default Context;

// TODO: Step2 Add reducer for fetch and outcome
const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_COUPLE_BEGIN':
      return {...state, fetchingCouple: true};
    case 'FETCH_COUPLE_FAILURE':
      return {
        ...state,
        registries: [],
        fetchingCouple: false,
      };
    case 'FETCH_COUPLE_SUCCESS':
      return {
        ...state,
        ...action,
        fetchingCouple: false,
      };

    case 'DELETE_REGISTRY_STARTED':
      return {
        ...state,
        registries: state.registries.map(registry => {
          if (registry.id !== action.registryId) {
            return registry;
          }

          return {
            ...registry,
            hasErrored: false,
            isSaving: true,
          };
        }),
      };

    case 'DELETE_REGISTRY_SUCCESS':
      return {
        ...state,
        registries: state.registries.filter(
          registry => registry.id !== action.registryId,
        ),
      };

    case 'DELETE_REGISTRY_FAILURE':
      return {
        ...state,
        registries: state.registries.map(registry => {
          if (registry.id !== action.registryId) {
            return registry;
          }

          return {
            ...registry,
            hasErrored: true,
            isSaving: false,
          };
        }),
      };

    case 'UPDATE_REGISTRY_HIDDEN_STATUS_STARTED':
      return {
        ...state,
        registries: state.registries.map(registry => {
          if (registry.id !== action.registryId) {
            return registry;
          }

          return {
            ...registry,
            hasErrored: false,
            isSaving: true,
          };
        }),
      };

    case 'UPDATE_REGISTRY_HIDDEN_STATUS_SUCCESS':
      return {
        ...state,
        selectedRegistry: {
          ...state.registries.find(
            registry => registry.id === action.registryId,
          ),
          hasErrored: false,
          hiddenCoupleSearch: action.isHidden,
          hiddenWws: action.isHidden,
        },
        registries: state.registries.map(registry => {
          if (registry.id !== action.registryId) {
            return registry;
          }

          return {
            ...registry,
            hasErrored: false,
            hiddenCoupleSearch: action.isHidden,
            hiddenWws: action.isHidden,
            isSaving: false,
          };
        }),
      };

    case 'UPDATE_REGISTRY_HIDDEN_STATUS_FAILURE':
      return {
        ...state,
        registries: state.registries.map(registry => {
          if (registry.id !== action.registryId) {
            return registry;
          }

          return {
            ...registry,
            hasErrored: true,
            isSaving: false,
          };
        }),
      };

    case 'UPDATE_MANUAL_REGISTRY_SUCCESS':
      return {
        ...state,
        registries: state.registries.map(registry => {
          if (registry.id !== action.registryId) {
            return registry;
          }

          return {
            ...registry,
            manualRegistryName: action.manualRegistryName,
            manualRegistryUrl: action.manualRegistryUrl,
          };
        }),
      };

    case 'UPDATE_SYNC_REGISTRY_PRODUCTS_STATUS':
      return {
        ...state,
        registries: state.registries.map(registry => {
          if (registry.id !== action.registryId) {
            return registry;
          }
          const {productSyncInfo} = registry;
          return {
            ...registry,
            productSyncInfo: {
              ...productSyncInfo,
              lastRunTime: action.lastRunTime,
              retailItemsApi: {
                ...productSyncInfo?.retailItemsApi,
                message: {
                  ...productSyncInfo?.retailItemsApi?.message,
                  status: action.status,
                },
              },
              retailer: {
                ...productSyncInfo?.retailer,
                message: {
                  ...productSyncInfo?.retailItemsApi?.message,
                  status: action.status,
                },
              },
            },
          };
        }),
      };
    default:
      return state;
  }
};

export const CoupleContextProvider = ({children}) => {
  const {memberId} = useContext(MemberContext);

  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchCouple = async () => {
    dispatch({type: 'FETCH_COUPLE_BEGIN'});
    try {
      const couple = await RegistryCoreApiService.getCouple(memberId);
      dispatch({
        type: 'FETCH_COUPLE_SUCCESS',
        coupleId: couple.id,
        gvrUrl: formatGvrUrl(couple),
        registries: couple.registries
          ? couple.registries.sort(sortByTypeTierAndName).map(registry => ({
              ...registry,
              isSaving: false,
              hasErrored: false,
            }))
          : [],
        wwsUrl: formatWwsUrl(couple),
        hasCashRegistry: getHasCashRegistry(couple),
        hasEverHadCashRegistry: getHasEverHadCashRegistry(couple),
        hasTransactionalRegistry: couple.hasTransactionalRegistry,
        fianceFirstName: couple.fianceFirstName,
        firstName: couple.firstName,
      });
    } catch (error) {
      Honeybadger.notify(error, 'GetCoupleError');

      dispatch({type: 'FETCH_COUPLE_FAILURE'});
    }
  };

  useEffect(() => {
    if (!memberId) return;
    fetchCouple();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const updateRegistryHiddenStatus = async (registryId, isHidden) => {
    try {
      dispatch({
        type: 'UPDATE_REGISTRY_HIDDEN_STATUS_STARTED',
        registryId,
      });

      await RegistryCoreApiService.updateRegistry(registryId, {
        hidden: isHidden,
      });

      dispatch({
        type: 'UPDATE_REGISTRY_HIDDEN_STATUS_SUCCESS',
        registryId,
        isHidden,
      });
    } catch (error) {
      Honeybadger.notify(error, 'UpdateRegistryHiddenError');

      dispatch({
        type: 'UPDATE_REGISTRY_HIDDEN_STATUS_FAILURE',
        registryId,
      });
    }
  };

  const deleteRegistry = async (registryId, purge) => {
    try {
      dispatch({
        type: 'DELETE_REGISTRY_STARTED',
        registryId,
      });

      await RegistryCoreApiService.deleteRegistry(registryId, purge);

      dispatch({
        type: 'DELETE_REGISTRY_SUCCESS',
        registryId,
      });
    } catch (error) {
      Honeybadger.notify(error, 'DeleteRegistryError');

      dispatch({
        type: 'DELETE_REGISTRY_FAILURE',
        registryId,
      });

      throw error;
    }
  };

  const updateManualRegistry = async ({
    manualRegistryName,
    manualRegistryUrl,
    registryId,
  }) => {
    if (!state || !state.coupleId) {
      throw new Error('Couple is not set. Cannot update registry');
    }

    await RegistryCoreApiService.updateManualRegistry({
      coupleId: state.coupleId,
      storeName: manualRegistryName,
      registryId,
      registryUrl: manualRegistryUrl,
    });

    dispatch({
      type: 'UPDATE_MANUAL_REGISTRY_SUCCESS',
      registryId,
      manualRegistryName,
      manualRegistryUrl,
    });
  };

  const updateSyncRegistryProductsStatus = async syncInfo => {
    dispatch({
      type: 'UPDATE_SYNC_REGISTRY_PRODUCTS_STATUS',
      registryId: syncInfo.registryId,
      status: syncInfo.status,
      lastRunTime: syncInfo.lastRunTime,
    });
  };

  return (
    <Context.Provider
      value={{
        coupleId: state.coupleId,
        fetchingCouple: state.fetchingCouple,
        fianceFirstName: state.fianceFirstName,
        firstName: state.firstName,
        gvrUrl: state.gvrUrl,
        hasCashRegistry: state.hasCashRegistry,
        hasEverHadCashRegistry: state.hasEverHadCashRegistry,
        registries: state.registries,
        deleteRegistry,
        updateManualRegistry,
        updateRegistryHiddenStatus,
        fetchCouple,
        updateSyncRegistryProductsStatus,
        wwsUrl: state.wwsUrl,
        hasTransactionalRegistry: state.hasTransactionalRegistry,
      }}>
      {children}
    </Context.Provider>
  );
};

CoupleContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
